import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '@frontend/contexts/AuthContext';
import { StyledTextField } from '@modules/ui-components';
import { useProviderCredentials } from '../hooks/useProviderCredentials';
import type {
  ProviderCredentialEnvironment,
  ProviderCredentialFieldStatus,
  ProviderCredentialProviderStatus,
} from '../types';

const TEST_IDS = {
  page: 'provider-credentials-admin-page',
  refresh: 'provider-credentials-refresh',
  environment: 'provider-credentials-environment',
  provider: (id: string) => `provider-credentials-provider-${id}`,
  field: (providerId: string, fieldKey: string) =>
    `provider-credentials-field-${providerId}-${fieldKey}`,
  save: (providerId: string) => `provider-credentials-save-${providerId}`,
  delete: (providerId: string, fieldKey: string) =>
    `provider-credentials-delete-${providerId}-${fieldKey}`,
};

function environmentColor(environment: ProviderCredentialEnvironment | null) {
  if (environment === 'prod') return 'error';
  if (environment === 'stage') return 'warning';
  if (environment === 'dev') return 'info';
  return 'default';
}

function formatUpdatedAt(value?: string): string {
  if (!value) return 'Never updated';
  return new Date(value).toLocaleString();
}

function fieldStatusLabel(field: ProviderCredentialFieldStatus): string {
  if (field.error) return 'Error';
  if (field.configured) return 'Configured';
  if (field.kind === 'config' && field.defaultValue) return 'Default';
  return 'Missing';
}

function fieldStatusColor(field: ProviderCredentialFieldStatus) {
  if (field.error) return 'error';
  if (field.configured) return 'success';
  if (field.kind === 'config' && field.defaultValue) return 'info';
  return field.required ? 'error' : 'default';
}

interface ProviderCardProps {
  provider: ProviderCredentialProviderStatus;
  saving: boolean;
  draft: Record<string, string>;
  onDraftChange: (fieldKey: string, value: string) => void;
  onSave: () => Promise<void>;
  onDelete: (fieldKey: string) => Promise<void>;
}

function ProviderCard({
  provider,
  saving,
  draft,
  onDraftChange,
  onSave,
  onDelete,
}: ProviderCardProps): React.ReactElement {
  const savePayload = useMemo(() => {
    const payload: Record<string, string> = {};
    for (const field of provider.fields) {
      const value = draft[field.key];
      if (field.kind === 'secret') {
        if (value?.trim()) payload[field.key] = value.trim();
      } else {
        const configValue = (value ?? field.value ?? field.defaultValue ?? '').trim();
        if (configValue) payload[field.key] = configValue;
      }
    }
    return payload;
  }, [draft, provider.fields]);

  const canSave = Object.keys(savePayload).length > 0 && !saving;

  return (
    <Paper
      variant="outlined"
      data-testid={TEST_IDS.provider(provider.id)}
      sx={{ p: 2, borderRadius: 1 }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <KeyIcon fontSize="small" color="action" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6">{provider.label}</Typography>
          <Typography variant="body2" color="text.secondary">
            {provider.description}
          </Typography>
        </Box>
        <Chip size="small" label={provider.category} variant="outlined" />
      </Stack>

      <Stack spacing={1.5}>
        {provider.fields.map((field) => {
          const value =
            field.kind === 'secret'
              ? draft[field.key] ?? ''
              : draft[field.key] ?? field.value ?? field.defaultValue ?? '';
          return (
            <Stack
              key={field.key}
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <StyledTextField
                data-testid={TEST_IDS.field(provider.id, field.key)}
                label={field.label}
                type={field.kind === 'secret' ? 'password' : 'text'}
                value={value}
                onChange={(nextValue) => onDraftChange(field.key, nextValue)}
                placeholder={field.kind === 'secret' ? 'Write-only value' : field.defaultValue}
                size="small"
                fullWidth
                helperText={
                  field.kind === 'secret'
                    ? `${field.envVar ?? field.key}${field.version ? ` · version ${field.version}` : ''}${field.error ? ` · ${field.error}` : ''}`
                    : field.envVar ?? field.key
                }
              />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 260 }}>
                <Chip
                  size="small"
                  color={fieldStatusColor(field) as any}
                  label={fieldStatusLabel(field)}
                  variant={field.configured ? 'filled' : 'outlined'}
                />
                {field.runtime === 'cloud-run-injected' && (
                  <Chip size="small" label="Redeploy" variant="outlined" />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {formatUpdatedAt(field.updatedAt)}
                </Typography>
                <Tooltip title="Delete credential">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!field.configured || saving}
                      onClick={() => onDelete(field.key)}
                      data-testid={TEST_IDS.delete(provider.id, field.key)}
                      aria-label={`delete ${field.label}`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          disabled={!canSave}
          onClick={onSave}
          data-testid={TEST_IDS.save(provider.id)}
        >
          Save
        </Button>
      </Stack>
    </Paper>
  );
}

export default function ProviderCredentialsAdminPage(): React.ReactElement {
  const { isAdmin } = useAuth();
  const {
    environment,
    providers,
    loading,
    saving,
    error,
    refresh,
    saveProvider,
    deleteField,
  } = useProviderCredentials();
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>>>({});
  const [success, setSuccess] = useState<string | null>(null);

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Admin access required.</Alert>
      </Box>
    );
  }

  const handleDraftChange = (providerId: string, fieldKey: string, value: string) => {
    setDrafts((current) => ({
      ...current,
      [providerId]: {
        ...(current[providerId] ?? {}),
        [fieldKey]: value,
      },
    }));
  };

  const handleSave = async (provider: ProviderCredentialProviderStatus) => {
    const draft = drafts[provider.id] ?? {};
    const payload: Record<string, string> = {};
    for (const field of provider.fields) {
      if (field.kind === 'secret') {
        const value = draft[field.key]?.trim();
        if (value) payload[field.key] = value;
      } else {
        const value = (draft[field.key] ?? field.value ?? field.defaultValue ?? '').trim();
        if (value) payload[field.key] = value;
      }
    }
    await saveProvider(provider.id, payload);
    setDrafts((current) => ({ ...current, [provider.id]: {} }));
    setSuccess(`${provider.label} credentials updated`);
  };

  const handleDelete = async (provider: ProviderCredentialProviderStatus, field: ProviderCredentialFieldStatus) => {
    const confirmed = window.confirm(`Delete ${provider.label}: ${field.label}?`);
    if (!confirmed) return;
    await deleteField(provider.id, field.key);
    setSuccess(`${provider.label}: ${field.label} deleted`);
  };

  return (
    <Box data-testid={TEST_IDS.page} sx={{ p: 3, maxWidth: 1180 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5">Provider credentials</Typography>
          <Typography variant="body2" color="text.secondary">
            Current environment only. Secret values are write-only.
          </Typography>
        </Box>
        <Chip
          data-testid={TEST_IDS.environment}
          size="small"
          color={environmentColor(environment) as any}
          label={environment ? environment.toUpperCase() : 'ENV'}
        />
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={refresh}
              disabled={loading || saving}
              aria-label="refresh"
              data-testid={TEST_IDS.refresh}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {providers.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              saving={saving}
              draft={drafts[provider.id] ?? {}}
              onDraftChange={(fieldKey, value) => handleDraftChange(provider.id, fieldKey, value)}
              onSave={() => handleSave(provider)}
              onDelete={(fieldKey) => {
                const field = provider.fields.find((candidate) => candidate.key === fieldKey);
                return field ? handleDelete(provider, field) : Promise.resolve();
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
